import { EditorConfig, LexicalEditor, LexicalNode } from 'lexical';
import { v4 as uuidv4 } from 'uuid';

// Import your reaction icons
import CommentPlusIcon from '../../../../assets/comment-plus.svg';
import ReactionArticleIcon from '../../../../assets/reaction-article-icon.svg';
import HeartReactionIcon from '../../../../assets/heart-reaction-icon.svg';
import SadReactionIcon from '../../../../assets/sad-reaction-icon.svg';
import AngryReactionIcon from '../../../../assets/angry-reaction-icon.svg';
import FunnyReactionIcon from '../../../../assets/funny-reaction-icon.svg';
import GalaxyBrainReactionIcon from '../../../../assets/galaxy-brain-reaction-icon.svg';
import InsightfulReactionIcon from '../../../../assets/insightful-reaction-icon.svg';
import NeedsCitationIcon from '../../../../assets/needs-citation.svg';
import BadLogicIcon from '../../../../assets/puzzle.svg';
import { UnauthorizedDispatcher } from 'src/utils/unauthorized-dispatcher';

export const REACTION_ICONS = [
  { type: 'like', icon: HeartReactionIcon, textDisplay: 'Like' },
  { type: 'funny', icon: FunnyReactionIcon, textDisplay: 'Funny' },
  { type: 'angry', icon: AngryReactionIcon, textDisplay: 'Angry' },
  { type: 'sad', icon: SadReactionIcon, textDisplay: 'Sad' },
  { type: 'innovative', icon: InsightfulReactionIcon, textDisplay: 'Insightful' },
  { type: 'big_brain', icon: GalaxyBrainReactionIcon, textDisplay: 'Big Brain' },
  { type: 'need_source', icon: NeedsCitationIcon, textDisplay: 'Needs Source' },
  { type: 'poor_logic', icon: BadLogicIcon, textDisplay: 'Poor Logic' }
];

export type CommentableNodeData = {
  nodeId: string;
  commentCount: number;
  reactionCount: number;
  nodeData: {
    reactionCount: number;
    isReacted: boolean;
    isCommented: boolean;
    reactedType?: string;
    comment?: {
      isCommented: boolean;
      commentCount: number;
    };
  };
};
export function createCommentableNode<T extends typeof LexicalNode>(BaseNode: T) {
  const userId = JSON.parse(localStorage.getItem('user'))?.id;

  // @ts-ignore
  return class CommentableNode extends BaseNode {
    __nodeId: string;
    __commentCount: number;
    __reactionCount: number;
    __nodeData: any;
    __icons: any;

    private __eventListeners: Array<{
      element: HTMLElement;
      type: string;
      listener: EventListener;
    }> = [];

    private __portalElements: HTMLElement[] = []; // Track portal elements (like hover-bar) for cleanup

    constructor(...args: any[]) {
      super(...args);
      this.__nodeId = uuidv4();
      this.__commentCount = 0;
      this.__reactionCount = 0;
      this.__nodeData = {};
      this.__portalElements = [];
    }

    addEventListenerWithCleanup(element: HTMLElement, type: string, listener: EventListener): void {
      element.addEventListener(type, listener);
      this.__eventListeners.push({ element, type, listener });
    }

    cleanupEventListeners(): void {
      this.__eventListeners.forEach(({ element, type, listener }) => {
        element.removeEventListener(type, listener);
      });
      this.__eventListeners = [];
    }

    // Minimum characters required for a paragraph to be reactable
    static MIN_CHARS_FOR_REACTIONS = 80;

    createDOM(config: EditorConfig, editor: LexicalEditor): HTMLElement {
      // Call the original createDOM method from the base class
      const element = super.createDOM(config, editor);
      const textContent = this.getTextContent();
      const trimmedLength = textContent?.trim().length || 0;
      // Require minimum character count for text-only paragraphs to be reactable
      const hasTextContent = trimmedLength >= (this.constructor as any).MIN_CHARS_FOR_REACTIONS;
      // Check if this paragraph contains media nodes (images, videos, audio)
      const children = (this as any).getChildren();
      const hasMediaContent = children.some((child: any) => {
        const childType = child.getType();
        return childType === 'image' || childType === 'video' || childType === 'audio';
      });

      // Extract media info while we're in the Lexical transaction
      const mediaInfo = hasMediaContent ? this.extractMediaInfo(children) : null;

      const hasContent = hasTextContent || hasMediaContent;

      if (!hasContent) {
        return element;
      }

      const isTopLevel = this.getParent()?.getType() === 'root';

      // Show interactions for top-level paragraphs OR paragraphs containing media
      // But only in the main article viewer, not in comment viewers or other contexts
      const isMainArticleViewer = config.namespace === 'Twygs-Viewer';
      const shouldShowInteractions =
        hasContent &&
        !config.theme.isEditable &&
        (isTopLevel || hasMediaContent) &&
        isMainArticleViewer;

      // Only create wrapper in viewer mode - in editor mode, return the element directly
      // to prevent Lexical from appending text children to the wrong element
      if (!shouldShowInteractions) {
        return element;
      }

      const wrapper = document.createElement('div');
      wrapper.classList.add('wrapper');
      wrapper.appendChild(element);

      if (shouldShowInteractions) {
        // ========== Interaction Container (shown on hover/long-press) ==========
        const interactionContainer = document.createElement('div');
        interactionContainer.classList.add('interaction-container');

        // Action buttons container
        const actionButtons = document.createElement('div');
        actionButtons.classList.add('action-buttons');

        // Add reaction button
        const reactionButton = this.createUserReactionButton(this.__nodeData);
        actionButtons.appendChild(reactionButton);

        // Add stats button (shows reaction/comment counts, opens modal)
        const statsButton = this.createStatsButton(this.__nodeData);
        actionButtons.appendChild(statsButton);

        // Add comment button
        const commentButton = this.createCommentActionButton();
        actionButtons.appendChild(commentButton);

        interactionContainer.appendChild(actionButtons);

        // Setup event listeners for buttons
        this.setupUserReactionButton(reactionButton);
        this.setupStatsButton(statsButton);

        const commentText = hasTextContent
          ? textContent
          : hasMediaContent
            ? this.getMediaDescription()
            : textContent;
        this.setupCommentActionButton(commentButton, commentText, mediaInfo);

        // ========== Mobile Long Press Detection ==========
        this.setupMobileLongPress(wrapper);

        // Listen for commentPosted event to update UI
        const handleCommentPosted = (e: Event) => {
          const customEvent = e as CustomEvent;
          if (customEvent.detail && customEvent.detail.nodeId === this.__nodeId) {
            commentButton.style.backgroundColor = '#4CAF50';
            const commentIcon = commentButton.querySelector('img');
            if (commentIcon) {
              (commentIcon as HTMLElement).style.filter = 'brightness(0) invert(1)';
            }
            editor.update(() => {
              const self = this.getWritable();
              self.__nodeData = {
                ...self.__nodeData,
                comment: {
                  ...(self.__nodeData?.comment || {}),
                  isCommented: true,
                  commentCount: (self.__nodeData?.comment?.commentCount || 0) + 1
                }
              };
            });
          }
        };
        window.addEventListener('commentPosted', handleCommentPosted);
        this.addEventListenerWithCleanup(window as any, 'commentPosted', handleCommentPosted);

        wrapper.appendChild(interactionContainer);
      }

      return wrapper;
    }

    // Implement all the helper methods from CommentableParagraphNode
    private createReactionSummary(nodeData: any): HTMLElement {
      const summaryContainer = document.createElement('div');
      summaryContainer.classList.add('reaction-summary');

      // Get all reaction types and counts from nodeData
      const reactionCounts = nodeData?.reactionCounts || {};
      const sortedReactions = Object.entries(reactionCounts)
        .filter(([type, count]: [string, any]) => count > 0)
        .sort(([, a]: [string, any], [, b]: [string, any]) => b - a); // Sort by count descending

      const hasReactions = sortedReactions.length > 0;

      if (hasReactions) {
        // Mark the interaction container as having reactions
        const interactionContainer = summaryContainer.closest(
          '.interaction-container'
        ) as HTMLElement;
        if (interactionContainer) {
          interactionContainer.classList.add('has-reactions');
        }

        // Create icons container for overlapping effect
        const iconsContainer = document.createElement('div');
        iconsContainer.classList.add('reaction-summary-icons');

        // Calculate total count
        let totalCount = 0;

        // Limit to top 3 reaction types to prevent overcrowding
        const maxIconsToShow = 3;
        sortedReactions.slice(0, maxIconsToShow).forEach(([type, count]: [string, any]) => {
          const reactionItem = document.createElement('div');
          reactionItem.classList.add('reaction-summary-item');

          const reactionIcon = this.getReactionIconByType(type);
          if (reactionIcon) {
            const img = document.createElement('img');
            img.src = reactionIcon.icon;
            img.alt = reactionIcon.textDisplay;
            img.classList.add('reaction-summary-icon');
            img.title = `${reactionIcon.textDisplay}: ${count}`; // Tooltip showing count

            reactionItem.appendChild(img);
            iconsContainer.appendChild(reactionItem);
          }
        });

        // Calculate total count from all reactions, not just displayed ones
        sortedReactions.forEach(([type, count]: [string, any]) => {
          totalCount += count;
        });

        // Add total count
        const totalSpan = document.createElement('span');
        totalSpan.textContent = totalCount.toString();
        totalSpan.classList.add('reaction-summary-total');

        summaryContainer.appendChild(iconsContainer);
        summaryContainer.appendChild(totalSpan);
      } else {
        // Hide the container when there are no reactions
        summaryContainer.style.display = 'none';
      }

      return summaryContainer;
    }

    private createUserReactionButton(nodeData: any): HTMLElement {
      const reactionButton = document.createElement('button');
      reactionButton.classList.add('user-reaction-button');
      reactionButton.title = 'React';

      const isReacted = nodeData?.isReacted && nodeData?.reactedType;

      if (isReacted) {
        // User has reacted - show their reaction
        reactionButton.classList.add('active');
        const selectedEmoji = this.getReactionIconByType(nodeData.reactedType);
        if (selectedEmoji) {
          const img = document.createElement('img');
          img.src = selectedEmoji.icon;
          img.alt = selectedEmoji.textDisplay;
          img.classList.add('user-reaction-icon');
          reactionButton.appendChild(img);
        }
      } else {
        // User hasn't reacted - show default reaction icon
        const img = document.createElement('img');
        img.src = ReactionArticleIcon;
        img.alt = 'React';
        img.classList.add('user-reaction-icon');
        reactionButton.appendChild(img);
      }

      // Create hover bar for reaction picker - appended to body to avoid overflow clipping
      const hoverBar = document.createElement('div');
      hoverBar.classList.add('hover-bar-portal'); // Only use portal class, not generic hover-bar
      hoverBar.style.position = 'fixed';
      hoverBar.style.zIndex = '99999';
      hoverBar.style.opacity = '0';
      hoverBar.style.visibility = 'hidden';
      hoverBar.style.pointerEvents = 'none';

      REACTION_ICONS.forEach((reactionIcon) => {
        const iconWrapper = document.createElement('div');
        iconWrapper.classList.add('icon-wrapper');
        iconWrapper.dataset.type = reactionIcon.type;

        const img = document.createElement('img');
        img.classList.add('reaction-img');
        img.src = reactionIcon.icon;
        img.alt = reactionIcon.textDisplay;

        const iconText = document.createElement('div');
        iconText.classList.add('icon-text');
        iconText.textContent = reactionIcon.textDisplay;

        iconWrapper.appendChild(img);
        iconWrapper.appendChild(iconText);
        hoverBar.appendChild(iconWrapper);
      });

      // Append hover bar to body (portal-like behavior)
      document.body.appendChild(hoverBar);

      // Position and show/hide hover bar on mouse events
      const showHoverBar = () => {
        const rect = reactionButton.getBoundingClientRect();
        const hoverBarWidth = hoverBar.offsetWidth || 300; // Estimate if not yet rendered

        // Position above the button, centered horizontally
        hoverBar.style.left = `${rect.left + rect.width / 2 - hoverBarWidth / 2}px`;
        hoverBar.style.top = `${rect.top - 50}px`; // 50px above button

        // Ensure it doesn't go off-screen left
        const leftPos = parseFloat(hoverBar.style.left);
        if (leftPos < 10) {
          hoverBar.style.left = '10px';
        }

        // Ensure it doesn't go off-screen right
        const rightEdge = leftPos + hoverBarWidth;
        if (rightEdge > window.innerWidth - 10) {
          hoverBar.style.left = `${window.innerWidth - hoverBarWidth - 10}px`;
        }

        hoverBar.style.opacity = '1';
        hoverBar.style.visibility = 'visible';
        hoverBar.style.pointerEvents = 'auto';

        // Keep the interaction container visible while hover bar is open
        const wrapper = reactionButton.closest('.wrapper');
        if (wrapper) {
          wrapper.classList.add('hover-bar-open');
        }
      };

      const hideHoverBar = () => {
        hoverBar.style.opacity = '0';
        hoverBar.style.visibility = 'hidden';
        hoverBar.style.pointerEvents = 'none';

        // Remove the class that keeps interaction container visible
        const wrapper = reactionButton.closest('.wrapper');
        if (wrapper) {
          wrapper.classList.remove('hover-bar-open');
        }
      };

      // Show on button hover
      reactionButton.addEventListener('mouseenter', showHoverBar);

      // Hide when leaving both button and hover bar
      let hideTimeout: NodeJS.Timeout;

      reactionButton.addEventListener('mouseleave', () => {
        hideTimeout = setTimeout(hideHoverBar, 150); // Slightly longer delay
      });

      hoverBar.addEventListener('mouseenter', () => {
        clearTimeout(hideTimeout);
        hoverBar.style.opacity = '1';
        hoverBar.style.visibility = 'visible';
        hoverBar.style.pointerEvents = 'auto';
      });

      hoverBar.addEventListener('mouseleave', hideHoverBar);

      // Store reference for cleanup
      (reactionButton as any).__hoverBar = hoverBar;
      this.__portalElements.push(hoverBar);

      return reactionButton;
    }

    private createCommentActionButton(): HTMLElement {
      const commentButton = document.createElement('button');
      commentButton.classList.add('comment-action-button');

      const commentCount = this.__nodeData?.comment?.commentCount || 0;
      const isCommented = this.__nodeData?.comment?.isCommented || false;

      if (isCommented) {
        commentButton.classList.add('active');
      }

      const commentIcon = document.createElement('img');
      commentIcon.src = CommentPlusIcon;
      commentIcon.alt = 'Comment';
      commentIcon.classList.add('comment-action-icon');
      commentButton.appendChild(commentIcon);

      if (commentCount > 0) {
        const countSpan = document.createElement('span');
        countSpan.textContent = commentCount.toString();
        countSpan.classList.add('comment-count-badge');
        commentButton.appendChild(countSpan);
      }

      return commentButton;
    }

    private setupReactionSummary(summaryElement: HTMLElement, articleId: string): void {
      const summaryHandler = (e: Event) => {
        e.stopPropagation();
        const event = new CustomEvent('openReactionModal', {
          bubbles: true,
          detail: {
            nodeId: this.__nodeId,
            reactionCount: this.__reactionCount,
            commentCount: this.__nodeData?.comment?.commentCount
          }
        });
        summaryElement.dispatchEvent(event);
      };

      this.addEventListenerWithCleanup(summaryElement, 'click', summaryHandler);
    }

    private setupUserReactionButton(button: HTMLElement): void {
      // Get hover bar from stored reference (it's in document.body, not the button)
      const hoverBar = (button as any).__hoverBar as HTMLElement;

      // Handle reaction icon clicks (to add/change reaction)
      const icons = hoverBar
        ? hoverBar.querySelectorAll('.icon-wrapper')
        : button.querySelectorAll('.icon-wrapper');

      icons.forEach((icon: HTMLElement) => {
        const clickHandler = (e: Event) => {
          if (!userId) return UnauthorizedDispatcher();
          e.preventDefault();
          e.stopPropagation();

          const reactionType = icon.dataset.type;
          if (!reactionType) return;

          const event = new CustomEvent('nodeReaction', {
            bubbles: true,
            detail: {
              nodeId: this.__nodeId,
              nodeKey: this.__key,
              reactionType,
              isLike: true
            }
          });
          button.dispatchEvent(event);

          // Close the hover bar after selecting a reaction
          if (hoverBar) {
            hoverBar.style.opacity = '0';
            hoverBar.style.visibility = 'hidden';
            hoverBar.style.pointerEvents = 'none';

            // Remove the hover-bar-open class from wrapper
            const wrapper = button.closest('.wrapper');
            if (wrapper) {
              wrapper.classList.remove('hover-bar-open');
            }
          }
        };

        this.addEventListenerWithCleanup(icon, 'click', clickHandler);
      });
    }

    // ========== Stats Button (shows total engagement, opens modal) ==========
    private createStatsButton(nodeData: any): HTMLElement {
      const statsButton = document.createElement('button');
      statsButton.classList.add('stats-action-button');
      statsButton.title = 'View reactions & comments';

      // Calculate totals
      const reactionCounts = nodeData?.reactionCounts || {};
      const totalReactions = Object.values(reactionCounts).reduce<number>(
        (sum, count: any) => sum + (count || 0),
        0
      );
      const commentCount = nodeData?.comment?.commentCount || 0;
      const totalEngagement = totalReactions + commentCount;

      // Show total count or a default icon
      if (totalEngagement > 0) {
        statsButton.classList.add('active');
        const countSpan = document.createElement('span');
        countSpan.classList.add('stats-count');
        countSpan.textContent = totalEngagement.toString();
        statsButton.appendChild(countSpan);
      } else {
        // Show "..." or info icon when no engagement
        statsButton.textContent = '•••';
        statsButton.style.fontSize = '8px';
        statsButton.style.letterSpacing = '1px';
      }

      return statsButton;
    }

    private setupStatsButton(button: HTMLElement): void {
      const clickHandler = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();

        const event = new CustomEvent('openReactionModal', {
          bubbles: true,
          detail: {
            nodeId: this.__nodeId,
            reactionCount: this.__reactionCount,
            commentCount: this.__nodeData?.comment?.commentCount
          }
        });
        button.dispatchEvent(event);
      };

      this.addEventListenerWithCleanup(button, 'click', clickHandler);
    }

    private setupCommentActionButton(
      button: HTMLElement,
      textContent: string,
      mediaInfo: any = null
    ): void {
      const commentHandler = (e: Event) => {
        e.stopPropagation();

        const event = new CustomEvent('openComments', {
          bubbles: true,
          detail: {
            nodeId: this.__nodeId,
            nodeText: textContent,
            commentCount: this.__nodeData?.comment?.commentCount,
            mediaInfo: mediaInfo // Use pre-extracted media information
          }
        });
        button.dispatchEvent(event);
      };

      this.addEventListenerWithCleanup(button, 'click', commentHandler);
    }

    // ========== iMessage-style Indicator ==========
    private createInteractionIndicator(reactionCount: number, commentCount: number): HTMLElement {
      const indicator = document.createElement('div');
      indicator.classList.add('interaction-indicator');

      // Show reaction icon if there are reactions
      if (reactionCount > 0) {
        const reactionIcon = document.createElement('img');
        reactionIcon.src = HeartReactionIcon;
        reactionIcon.alt = 'Reactions';
        reactionIcon.classList.add('interaction-indicator-icon');
        indicator.appendChild(reactionIcon);

        const reactionCountSpan = document.createElement('span');
        reactionCountSpan.textContent = reactionCount.toString();
        reactionCountSpan.classList.add('interaction-indicator-count');
        indicator.appendChild(reactionCountSpan);
      }

      // Add separator if both exist
      if (reactionCount > 0 && commentCount > 0) {
        const separator = document.createElement('span');
        separator.textContent = '·';
        separator.style.color = '#9ca3af';
        separator.style.fontSize = '10px';
        separator.style.margin = '0 2px';
        indicator.appendChild(separator);
      }

      // Show comment icon if there are comments
      if (commentCount > 0) {
        const commentIcon = document.createElement('img');
        commentIcon.src = CommentPlusIcon;
        commentIcon.alt = 'Comments';
        commentIcon.classList.add('interaction-indicator-icon');
        indicator.appendChild(commentIcon);

        const commentCountSpan = document.createElement('span');
        commentCountSpan.textContent = commentCount.toString();
        commentCountSpan.classList.add('interaction-indicator-count');
        indicator.appendChild(commentCountSpan);
      }

      return indicator;
    }

    private setupIndicatorClick(indicator: HTMLElement, articleId: string): void {
      const clickHandler = (e: Event) => {
        e.stopPropagation();
        // Open reaction modal to show who reacted
        const event = new CustomEvent('openReactionModal', {
          bubbles: true,
          detail: {
            nodeId: this.__nodeId,
            reactionCount: this.__reactionCount,
            commentCount: this.__nodeData?.comment?.commentCount
          }
        });
        indicator.dispatchEvent(event);
      };

      this.addEventListenerWithCleanup(indicator, 'click', clickHandler);
    }

    // ========== Mobile Long Press Detection ==========
    private setupMobileLongPress(wrapper: HTMLElement): void {
      let longPressTimer: ReturnType<typeof setTimeout> | null = null;
      let isLongPress = false;
      const LONG_PRESS_DURATION = 500; // ms

      const touchStartHandler = (e: TouchEvent) => {
        isLongPress = false;
        longPressTimer = setTimeout(() => {
          isLongPress = true;
          wrapper.classList.add('touch-active');
          // Subtle haptic feedback visual
          wrapper.style.transform = 'scale(0.98)';
          setTimeout(() => {
            wrapper.style.transform = '';
          }, 100);
        }, LONG_PRESS_DURATION);
      };

      const touchEndHandler = (e: TouchEvent) => {
        if (longPressTimer) {
          clearTimeout(longPressTimer);
          longPressTimer = null;
        }
        // Keep touch-active class for a bit so user can interact with buttons
        if (isLongPress) {
          setTimeout(() => {
            // Only remove if not hovering over interaction container
            const interactionContainer = wrapper.querySelector('.interaction-container');
            if (interactionContainer && !interactionContainer.matches(':hover')) {
              wrapper.classList.remove('touch-active');
            }
          }, 3000); // Keep visible for 3 seconds
        }
      };

      const touchMoveHandler = (e: TouchEvent) => {
        // Cancel long press if user moves finger
        if (longPressTimer) {
          clearTimeout(longPressTimer);
          longPressTimer = null;
        }
      };

      // Close when tapping elsewhere
      const documentTouchHandler = (e: TouchEvent) => {
        if (!wrapper.contains(e.target as Node)) {
          wrapper.classList.remove('touch-active');
        }
      };

      this.addEventListenerWithCleanup(wrapper, 'touchstart', touchStartHandler as EventListener);
      this.addEventListenerWithCleanup(wrapper, 'touchend', touchEndHandler as EventListener);
      this.addEventListenerWithCleanup(wrapper, 'touchmove', touchMoveHandler as EventListener);
      this.addEventListenerWithCleanup(
        document as any,
        'touchstart',
        documentTouchHandler as EventListener
      );
    }

    private getTotalCount(): string {
      const nodeReactionCount = this.__nodeData?.reactionCount || 0;
      const commentCount = this.__nodeData?.comment?.commentCount || 0;
      const totalCount = nodeReactionCount + commentCount;

      return totalCount > 0 ? totalCount.toString() : '';
    }

    private getReactionIconByType(type: string) {
      return REACTION_ICONS.find((emoji) => emoji.type === type);
    }

    private extractMediaInfo(children: any[]): any {
      // Extract media information for the comment sidebar
      const mediaItems = [];

      for (const child of children) {
        const childType = child.getType();
        if (childType === 'image') {
          mediaItems.push({
            type: 'image',
            src: child.getSrc(),
            altText: child.getAltText && child.getAltText(),
            width: child.__width,
            height: child.__height
          });
        } else if (childType === 'video') {
          mediaItems.push({
            type: 'video',
            src: child.getSrc(),
            altText: 'Video',
            width: child.__width,
            height: child.__height
          });
        } else if (childType === 'audio') {
          mediaItems.push({
            type: 'audio',
            src: child.getSrc(),
            altText: 'Audio',
            title: child.getTitle && child.getTitle()
          });
        }
      }

      return mediaItems.length > 0 ? mediaItems : null;
    }

    private getMediaDescription(): string {
      // Generate a description based on the media content in this paragraph
      const children = (this as any).getChildren();
      const mediaTypes = [];

      for (const child of children) {
        const childType = child.getType();
        if (childType === 'image') {
          mediaTypes.push('Image');
        } else if (childType === 'video') {
          mediaTypes.push('Video');
        } else if (childType === 'audio') {
          mediaTypes.push('Audio');
        }
      }

      if (mediaTypes.length === 1) {
        return mediaTypes[0];
      } else if (mediaTypes.length > 1) {
        return `Media (${mediaTypes.join(', ')})`;
      }

      return 'Media content';
    }

    setNodeData(nodeData: any): void {
      const self = this.getWritable();
      // Create a deep copy to ensure Lexical detects the change
      self.__nodeData = JSON.parse(JSON.stringify(nodeData));
    }

    getNodeData(): any {
      const self = this.getLatest();
      return self.__nodeData;
    }

    setReactionCount(count: number): void {
      const self = this.getWritable();
      self.__reactionCount = count;
    }

    getReactionCount(): number {
      const self = this.getLatest();
      return self.__reactionCount;
    }

    destroy(): void {
      this.cleanupEventListeners();
      // Remove portal elements from DOM
      this.__portalElements.forEach((el) => {
        if (el.parentNode) {
          el.parentNode.removeChild(el);
        }
      });
      this.__portalElements = [];
    }
  };
}
